// src/padplanner/editorSave.js
//
// Saving the editor's features (S4a's PUT, D-S4-7). The page holds one
// FeatureSaver per load.
//
//   * BATCH, AGAINST THE TOKEN. Every save sends the complete list with the
//     token the last GET or save returned. The response's token is the next
//     base.
//   * AUTOSAVE IS DEBOUNCED: a save goes out DEBOUNCE_MS after the last
//     change, and Save sends at once. One save is in flight at a time; a
//     change made while it is out is saved after it returns.
//   * 409 revision_changed STOPS SAVING. Someone else wrote this revision
//     (another tab, a perimeter edit). Nothing was written, there is no
//     merge, and nothing is sent again: the page keeps the user's work on
//     screen and says so until they reload.
//   * ANY OTHER REFUSAL is shown with the server's code; the work stays
//     unsaved, and the next change tries again.

export const DEBOUNCE_MS = 1500;

export default class FeatureSaver {
  /**
   * @param {object} o
   * @param {string} o.planId
   * @param {string} o.base                    the token from GET features
   * @param {(id: string, body: object) => Promise<object>} o.save
   * @param {() => object[]} o.getPayload      the editor's complete list
   * @param {(response: object, sent: object[]) => void} o.onSaved
   * @param {(state: object) => void} o.onState
   */
  constructor({ planId, base, save, getPayload, onSaved, onState, debounceMs = DEBOUNCE_MS }) {
    Object.assign(this, { planId, save, getPayload, onSaved, onState, debounceMs });
    this.base = base;
    this.seq = 0;          // bumped by every change
    this.savedSeq = 0;     // the seq the last successful save covered
    this.timer = null;
    this.inFlight = false;
    this.state = { status: "saved", savedAt: null, error: null };
    this.stopped = false;
  }

  get dirty() { return this.seq !== this.savedSeq; }

  _set(patch) {
    this.state = { ...this.state, ...patch };
    this.onState?.(this.state);
  }

  /** The editor changed. */
  changed() {
    if (this.stopped) return;
    this.seq += 1;
    if (this.state.status === "conflict") return;
    this._set({ status: this.inFlight ? "saving" : "dirty" });
    this._schedule();
  }

  _schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; this.saveNow(); }, this.debounceMs);
  }

  /** Save at once (the Save button, and the debounce). */
  async saveNow() {
    if (this.stopped || this.state.status === "conflict") return;
    clearTimeout(this.timer);
    this.timer = null;
    if (this.inFlight || !this.dirty) return;
    const seq = this.seq;
    const sent = this.getPayload();
    this.inFlight = true;
    this._set({ status: "saving", error: null });
    try {
      const response = await this.save(this.planId, { base: this.base, features: sent });
      if (this.stopped) return;
      this.base = response.token;
      this.savedSeq = seq;
      this.onSaved?.(response, sent);
      this._set({ status: this.dirty ? "dirty" : "saved", savedAt: response.plan?.updated_at ?? this.state.savedAt, error: null });
    } catch (err) {
      if (this.stopped) return;
      const error = { status: err?.status ?? null, code: err?.code ?? null, message: err?.message ?? "The save failed." };
      this._set({ status: err?.status === 409 && err?.code === "revision_changed" ? "conflict" : "error", error });
      return;
    } finally {
      this.inFlight = false;
    }
    if (this.dirty) this._schedule();
  }

  /** Nothing unsaved, nothing in flight, no conflict: a revision write may go out. */
  get idle() {
    return !this.stopped && !this.inFlight && !this.dirty && this.state.status !== "conflict";
  }

  /**
   * A write to the revision that moves its token, from the page that holds
   * it (S-e slice 3: the editor's imagery pin). It goes out only while the
   * saver is idle, with the saver's base (`write(base)`, the PATCH's base),
   * and the saver saves on from the token it answers. Changes made while it
   * is out wait for it, as they wait for a save. 409 revision_changed is the
   * saver's conflict, as a save's is; any other failure is thrown, the saver
   * as it was.
   * @param {(base: string) => Promise<{token?: string}>} write
   * @returns {Promise<object|null>} the answer, or null when not sent or a conflict
   */
  async writeRevision(write) {
    if (!this.idle) return null;
    this.inFlight = true;
    try {
      const response = await write(this.base);
      if (this.stopped) return null;
      if (response?.token) this.base = response.token;
      return response;
    } catch (err) {
      if (this.stopped) return null;
      if (err?.status === 409 && err?.code === "revision_changed") {
        this._set({ status: "conflict", error: { status: 409, code: err.code, message: err.message ?? "The revision changed." } });
        return null;
      }
      throw err;
    } finally {
      this.inFlight = false;
      if (this.dirty && !this.stopped && this.state.status !== "conflict") this._schedule();
    }
  }

  /** After a reload: a new base, nothing unsaved. */
  reset(base, savedAt = null) {
    clearTimeout(this.timer);
    this.timer = null;
    this.base = base;
    this.savedSeq = this.seq;
    this._set({ status: "saved", error: null, ...(savedAt ? { savedAt } : {}) });
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.timer);
  }
}
